import { IconContext } from '@phosphor-icons/react'
import '@robotensor/ui/styles.css'
import { toast, UIProvider } from '@robotensor/ui'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { APP_SITE } from './app/appSite.tsx'
import { router } from './app/router.tsx'
import { SiteProvider } from './app/site.tsx'
import { client } from './lib/api.ts'
import { appConfig } from './lib/appConfig.ts'
import { queryClient } from './lib/query.ts'
import './styles/console.css'
// The contributor app's own look (light "Pocket", dark "Night"), scoped to <html data-site="app">; ops never loads it.
import './styles/app.css'
// The November 2026 redesign: the N1 bar, Home, Robot, the session, Account, Welcome (`w-` classes).
import './styles/shell.css'

// A session that ends while the console is open (expired, revoked elsewhere) returns to sign-in
// with the current location kept, so signing back in lands exactly here.
client.subscribe((_session, event) => {
  if (event !== 'expired') return
  queryClient.clear()
  const here = router.state.location.href
  if (!router.state.location.pathname.startsWith('/signin')) {
    toast({ title: 'Your session ended', description: 'Sign in again to continue where you were.', tone: 'warn' })
    void router.navigate({ to: '/signin', search: { next: here, reason: 'expired' } })
  }
})

// A tab opened before a deploy asks for files of the old build. When one of them can't load (a page's code or CSS),
// load the current build once instead of showing the page half styled; a second failure within a minute is left
// to the error page, so a real outage can't loop.
window.addEventListener('vite:preloadError', (e) => {
  const KEY = 'rt.reloadedForBuild'
  let last = 0
  try {
    last = Number(sessionStorage.getItem(KEY) ?? 0)
  } catch {
    /* storage blocked: reload anyway, once per page */
  }
  if (Date.now() - last < 60_000) return
  try {
    sessionStorage.setItem(KEY, String(Date.now()))
  } catch {
    /* ignore */
  }
  e.preventDefault()
  window.location.reload()
})

// The server's app config (features, rules, notices, minimum versions): now, then on window focus at most once a
// minute; the last good copy covers offline starts and the built-in defaults cover everything else.
appConfig.start()

const icons = { weight: 'light' as const, size: 16, color: 'currentColor', mirrored: false }

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <IconContext.Provider value={icons}>
      <QueryClientProvider client={queryClient}>
        <UIProvider forcedTheme="light">
          <SiteProvider site={APP_SITE}>
            <RouterProvider router={router} />
          </SiteProvider>
        </UIProvider>
      </QueryClientProvider>
    </IconContext.Provider>
  </StrictMode>,
)
